import { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';

interface InfiniteScrollSentinelProps {
  onLoadMore: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading?: boolean;
  rootElement?: HTMLElement | null;
  rootMargin?: string;
  debounceMs?: number;
}

export function InfiniteScrollSentinel({
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  isLoading = false,
  rootElement,
  rootMargin = '200px',
  debounceMs = 300,
}: InfiniteScrollSentinelProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(() => {
            onLoadMore();
          }, debounceMs);
        }
      },
      {
        root: rootElement || null,
        rootMargin,
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [hasNextPage, isFetchingNextPage, isLoading, onLoadMore, rootElement, rootMargin, debounceMs]);

  if (!hasNextPage) return null;

  return (
    <div ref={sentinelRef} className="flex justify-center py-4">
      {isFetchingNextPage && (
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Carregando mais registros...</span>
        </div>
      )}
    </div>
  );
}
